import { useEffect, useState } from 'react'
import {
  ArrowLeft,
  Check,
  CircleAlert,
  CircleDashed,
  ClipboardList,
  Clock,
  LoaderCircle,
  MinusCircle,
  RefreshCw,
  ShieldAlert,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'

import { CostNote } from '@/components/CostNote'
import { Icon } from '@/components/Icon'
import { NoticeList } from '@/components/NoticeList'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { ErrorNote } from '@/components/ErrorNote'
import { DraftNote } from '@/features/rulebase/DraftBadge'
import { DATA_MODE_LABEL, type DataMode } from '@/features/workspace/dataModes'
import { useI18n, type TranslationKey } from '@/i18n'
import { buildPath, navigate } from '@/lib/router'
import { cn } from '@/lib/utils'
import { useRunPolling } from '@/lib/useRunPolling'
import type { Run, StageState } from '@/api/types'

const STAGE_LOOK: Record<StageState, { icon: LucideIcon; label: TranslationKey; className: string }> = {
  pending: { icon: CircleDashed, label: 'stage.pending', className: 'text-muted-2' },
  running: { icon: LoaderCircle, label: 'stage.running', className: 'text-accent' },
  done: { icon: Check, label: 'stage.done', className: 'text-signal-green-text' },
  skipped: { icon: MinusCircle, label: 'stage.skipped', className: 'text-muted' },
  failed: { icon: CircleAlert, label: 'stage.failed', className: 'text-signal-red-text' },
}

/**
 * The notice code the backend attaches to a run whose pack is a draft. Compared
 * as a plain string on purpose: the code union is generated, and a run must not
 * lose its draft label just because the schema on disk is a regeneration behind.
 */
const DRAFT_PACK_NOTICE = 'draft_pack'

/** The backend's own sentence for the draft state, or null when there is none. */
function draftPackMessage(run: Run | null): string | null {
  const notice = (run?.notices ?? []).find(
    (candidate) => (candidate.code as string) === DRAFT_PACK_NOTICE,
  )
  return notice ? notice.message_de : null
}

/** After this many minutes a still-moving run gets the "unusually long" note. */
export const LONG_RUNNING_MINUTES = 10
const ELAPSED_TICK_MS = 30_000

/**
 * Whole minutes since `since`, re-read every 30 s while `active`. Null when
 * there is nothing to count (no run yet, or a terminal one). Honest by
 * construction: it is arithmetic on the timestamps the API reports, not a
 * progress estimate.
 */
function useElapsedMinutes(since: string | null | undefined, active: boolean): number | null {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    if (!active) return
    // The tick is the only writer; the first value comes from the initialiser
    // above, so nothing is set synchronously inside the effect.
    const timer = setInterval(() => setNow(Date.now()), ELAPSED_TICK_MS)
    return () => clearInterval(timer)
  }, [active])
  if (!active || !since) return null
  const start = Date.parse(since)
  if (Number.isNaN(start)) return null
  return Math.max(0, Math.floor((now - start) / 60_000))
}

const RUN_STATUS_LABEL: Record<Run['status'], TranslationKey> = {
  queued: 'run.status.queued',
  running: 'run.status.running',
  succeeded: 'run.status.succeeded',
  failed: 'run.status.failed',
}

/**
 * Live progress view. It renders the stage list the API reports — including
 * stages the backend honestly marks `skipped` with a reason — and polls until
 * the run is terminal. There is no fake progress bar and no spinner that keeps
 * spinning after the backend has stopped.
 */
export function RunProgress({
  runId,
  initialRun,
  onOpenReport,
  onBack,
}: {
  runId: string
  initialRun: Run | null
  onOpenReport: (runId: string) => void
  onBack: () => void
}) {
  const { t } = useI18n()
  const { run, error } = useRunPolling(runId, initialRun)
  const moving = run ? run.status === 'queued' || run.status === 'running' : false
  const elapsed = useElapsedMinutes(run?.started_at ?? run?.created_at, moving)

  return (
    <div className="flex max-w-[900px] flex-col gap-8">
      <div className="flex flex-col gap-3">
        <Button variant="ghost" size="sm" className="self-start" onClick={onBack}>
          <Icon icon={ArrowLeft} />
          {t('common.back')}
        </Button>
        <h1 className="font-display text-[32px] leading-tight font-semibold">{t('run.title')}</h1>
        <p className="max-w-[70ch] text-[15px] text-muted">{t('run.lead')}</p>
        <dl className="flex flex-wrap gap-x-8 gap-y-2 text-[13px]">
          <div className="flex gap-2">
            <dt className="text-muted">{t('run.id')}</dt>
            <dd className="num text-ink-2">{runId}</dd>
          </div>
          {run ? (
            <>
              <div className="flex gap-2">
                <dt className="text-muted">{t('run.dossier')}</dt>
                <dd className="num text-ink-2">{run.dossier_id}</dd>
              </div>
              <div className="flex gap-2">
                <dt className="text-muted">{t('run.statusLabel')}</dt>
                <dd className="text-ink-2">{t(RUN_STATUS_LABEL[run.status])}</dd>
              </div>
              {elapsed !== null ? (
                <div className="flex gap-2" data-testid="run-elapsed">
                  <dt className="text-muted">
                    <Icon icon={Clock} className="me-1 inline size-3.5 align-[-2px]" />
                  </dt>
                  <dd className="text-ink-2">{t('run.elapsed', { minutes: String(elapsed) })}</dd>
                </div>
              ) : null}
              <div className="flex gap-2">
                <dt className="text-muted">{t('workspace.mode.label')}</dt>
                <dd className="text-ink-2" data-testid="run-data-mode">
                  {t(DATA_MODE_LABEL[run.data_mode as DataMode] ?? 'workspace.mode.unknown')}
                </dd>
              </div>
            </>
          ) : null}
        </dl>
      </div>

      {/* The FADP interlock, as the run itself recorded it. An uploaded dossier
          whose LLM step was blocked must say so on the progress view — that is
          where the reader is while it happens, and "fields are missing because
          the model was not allowed to look" is not a footnote. */}
      {run && !run.llm_allowed ? (
        <div
          role="note"
          data-testid="extraction-note"
          className="flex items-start gap-3 rounded-sm border border-border bg-signal-info-soft px-4 py-3 text-[13px] text-signal-info"
        >
          <Icon icon={ShieldAlert} className="mt-0.5" />
          <span>{run.extraction_note ?? t('run.llmBlocked')}</span>
        </div>
      ) : null}

      {/* A run that has been moving for a long time says so. The poller alone
          cannot tell "slow" from "abandoned" — a worker that died mid-run (or a
          server restart) left rows `running` for ever until 2026-09-16, and the
          only thing the reader saw was a spinner. The backend now fails such
          rows at start-up; this note tells the reader what to expect meanwhile. */}
      {elapsed !== null && elapsed >= LONG_RUNNING_MINUTES ? (
        <div
          role="note"
          data-testid="long-running-note"
          className="flex items-start gap-3 rounded-sm border border-border bg-signal-amber-soft px-4 py-3 text-[13px] text-signal-amber-text"
        >
          <Icon icon={Clock} className="mt-0.5" />
          <span>{t('run.longRunning')}</span>
        </div>
      ) : null}

      {/* A re-check is visibly a re-check OF something — the parent run is a
          link, not a footnote. Without it a reader lands on a run with three
          skipped stages and no way to tell why it looks so short. */}
      {run?.mode === 'recheck' ? (
        <div
          role="note"
          data-testid="recheck-run-banner"
          className="flex flex-wrap items-center gap-3 rounded-sm border border-border bg-surface px-4 py-3 text-[13px] text-ink-2"
        >
          <Icon icon={RefreshCw} />
          <span>{t('run.isRecheck')}</span>
          {run.parent_run_id ? (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => navigate(buildPath('run', { runId: run.parent_run_id! }))}
            >
              {t('run.openParent')}
            </Button>
          ) : null}
        </div>
      ) : null}

      {/* M4 — indicative mode, visibly. The backend attaches a `draft_pack`
          notice to a run standing on a draft; the notice carries the reason,
          this carries the label, in the same dashed grey it has on every other
          surface. Both, because a reader scanning the page for state should not
          have to read a notice list to learn the Gemeinde is not verified. */}
      {draftPackMessage(run) ? <DraftNote message={draftPackMessage(run)} /> : null}

      {/* Typed notices from the run itself: degraded extraction, missing pack,
          Gemeinde mismatch, budget … A degraded run must never look clean. */}
      <NoticeList notices={run?.notices} title={t('notices.runTitle')} />

      {error ? <ErrorNote message={`${t('run.pollError')} ${error}`} /> : null}

      <Card>
        <CardHeader>
          <CardTitle>{t('run.stagesTitle')}</CardTitle>
        </CardHeader>
        <CardContent>
          <ol
            className="flex flex-col"
            aria-live="polite"
            aria-busy={run ? run.status === 'running' || run.status === 'queued' : true}
          >
            {(run?.stages ?? []).map((stage) => {
              const look = STAGE_LOOK[stage.status]
              return (
                <li
                  key={stage.key}
                  data-stage={stage.key}
                  data-status={stage.status}
                  className="flex items-start gap-3 border-b border-border py-3 last:border-b-0"
                >
                  <Icon
                    icon={look.icon}
                    className={cn(
                      'mt-0.5 size-4',
                      look.className,
                      stage.status === 'running' && 'motion-safe:animate-spin',
                    )}
                  />
                  <div className="flex min-w-0 flex-col">
                    <span className="text-[14px] font-medium text-ink">{stage.label_de}</span>
                    {stage.reason ? (
                      <span className="text-[12px] text-muted">{stage.reason}</span>
                    ) : null}
                  </div>
                  {/* Status as text, not just as an icon colour. */}
                  <span className="ms-auto shrink-0 text-[12px] text-muted">{t(look.label)}</span>
                </li>
              )
            })}
            {!run ? <li className="py-3 text-[13px] text-muted">{t('common.loading')}</li> : null}
          </ol>
        </CardContent>
      </Card>

      {run?.status === 'failed' ? (
        <ErrorNote message={`${t('run.failedTitle')}: ${run.error ?? t('common.error')}`} />
      ) : null}

      <div className="flex flex-wrap gap-3">
        {run?.report_available ? (
          <Button onClick={() => onOpenReport(runId)}>{t('run.openReport')}</Button>
        ) : null}
        {run ? (
          <Button
            variant="secondary"
            onClick={() =>
              navigate(
                buildPath('facts', {
                  workspaceId: run.workspace_id,
                  dossierId: run.dossier_id,
                }),
              )
            }
          >
            <Icon icon={ClipboardList} />
            {t('facts.cta')}
          </Button>
        ) : null}
      </div>

      {/* Founder-only. `cost` is null for a prospect, and null renders nothing
          at all — not a zero, not an empty row. */}
      <CostNote cost={run?.cost} />
    </div>
  )
}
